/**
 * `IconButton`'s look (DESIGN_SYSTEM.md §5.2): a square control holding one icon, in three variants and the
 * Button's two heights. `root` is the `<button>`: layout only (§4.2); the paint is on `surface`.
 */
import type { ActionRole, Theme } from "@rista/ui/library/theme/theme";
import { defineVariants } from "@rista/ui/library/theme/variants";

export type IconButtonVariant = "ghost" | "secondary" | "primary";

const filled = (role: (t: Theme) => ActionRole) => (t: Theme) => ({
  surface: { backgroundColor: role(t).bg, borderColor: role(t).border },
  icon: { color: role(t).fg },
});

const groundOf = (t: Theme, variant: IconButtonVariant | undefined, state: "hover" | "pressed") => {
  if (variant === "primary" || variant === "secondary") return state === "hover" ? t.color.action[variant].bgHover : t.color.action[variant].bgPressed;
  return state === "hover" ? t.color.surface.hover : t.color.surface.pressed;
};

/** the icon's height in each size */
export const ICON_BUTTON_GLYPH = { sm: 20, md: 24 } as const;

/** the button's side in each size; a row that leaves a button's place empty reads it, so the two cannot drift */
export const ICON_BUTTON_SIZE = { sm: 36, md: 44 } as const;

export const iconButtonLook = defineVariants({
  slots: ["root", "surface", "icon"],
  base: (t) => ({
    // never shrinks: a squeezed icon button clips its picture
    root: { flexGrow: 0, flexShrink: 0, flexBasis: "auto" },
    surface: { alignItems: "center", justifyContent: "center", borderRadius: t.radius.control, borderWidth: 1, borderColor: "transparent" },
    icon: { color: t.color.text.primary },
  }),
  variants: {
    variant: {
      ghost: (t) => ({ icon: { color: t.color.text.primary } }),
      secondary: filled((t) => t.color.action.secondary),
      primary: filled((t) => t.color.action.primary),
    },
    size: {
      sm: () => ({ surface: { width: ICON_BUTTON_SIZE.sm, height: ICON_BUTTON_SIZE.sm } }),
      md: () => ({ surface: { width: ICON_BUTTON_SIZE.md, height: ICON_BUTTON_SIZE.md } }),
    },
  },
  states: {
    hovered: (t, v) => ({ surface: { backgroundColor: groundOf(t, v.variant as IconButtonVariant, "hover") } }),
    focused: (t) => ({ surface: { borderColor: t.color.border.focus } }),
    pressed: (t, v) => ({ surface: { backgroundColor: groundOf(t, v.variant as IconButtonVariant, "pressed") } }),
    disabled: () => ({ root: { opacity: 0.5 } }),
  },
  defaults: { variant: "ghost", size: "md" },
});
